import React, { useEffect, useState } from 'react';
import { Dropdown, Input } from '@cokitchen/cokitchen-components';
import { ReactComponent as MenuIcon } from 'assets/icons/menu.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as BlacklistRiderIcon } from 'assets/icons/blacklist-rider.svg';
import { ReactComponent as NoteIcon } from 'assets/icons/note.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as WalletIcon } from 'assets/icons/empty-wallet.svg';
import DeleteRiderModal from '../partner-modals/delete-rider-modal';
import RiderDetailsSideModal from '../partner-modals/rider-details-modal';
import DeductAmountModal from '../partner-modals/deduct-amount-modal';
import EditRiderDetailsModal from '../edit-rider-details';
import { EditRiderType, RiderType } from 'models/rider.model';
import BlacklistRiderModal from 'components/blacklist-rider-modal';
import { BaseDropdownListType, withDropdownList } from 'components/base/dropdown-list';
import { getUserName } from 'utils/functions';

type RiderInfoProps = {
  riders: RiderType[];
  onReloadRiders: () => void;
};

type DropdownListType = BaseDropdownListType & {
  key: string;
  className?: string;
};

const DropdownList = withDropdownList<DropdownListType>();

const RidersInfo = ({ riders, onReloadRiders }: RiderInfoProps) => {
  const [bulkRiders, setBulkRiders] = useState<RiderType[]>([]);
  const [showDeleteRiderModal, setShowDeleteRiderModal] = useState(false);
  const [showBlacklistRiderModal, setShowBlacklistRiderModal] = useState(false);
  const [showRiderDetails, setShowRiderDetails] = useState(false);
  const [showEditRiderDetails, setShowEditRiderDetails] = useState(false);
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [singleRider, setSingleRider] = useState<RiderType | null>(null);
  const [selectedRiders, setSelectedRiders] = useState<RiderType[]>([]);

  const viewDetails = () => {
    setShowRiderDetails(!showRiderDetails);
  };
  const editRiders = () => {
    setShowEditRiderDetails(!showEditRiderDetails);
  };
  const blacklistRider = (isSuccess?: boolean) => {
    if (isSuccess) onReloadRiders();
    setShowBlacklistRiderModal(!showBlacklistRiderModal);
  };
  const deleteRiders = (isSuccess?: boolean) => {
    if (isSuccess) onReloadRiders();
    setShowDeleteRiderModal(!showDeleteRiderModal);
  };
  const deductAmount = () => {
    setShowDeductModal(!showDeductModal);
  };

  const getListOptions = (rider?: RiderType) => {
    if (bulkRiders.length > 1)
      return [
        {
          name: 'Delete Riders',
          icon: <TrashIcon />,
          key: 'delete',
          className: 'text-[#E60A2B]'
        }
      ];
    return [
      {
        name: 'View details',
        icon: <NoteIcon />,
        key: 'view-details'
      },
      {
        name: 'Edit details',
        icon: <EditIcon />,
        key: 'edit-details'
      },
      rider?.rider_blacklist
        ? {
            name: 'Unblacklist rider',
            icon: <BlacklistRiderIcon />,
            key: 'blacklist'
          }
        : {
            name: 'Blacklist rider',
            icon: <BlacklistRiderIcon />,
            key: 'blacklist'
          },
      {
        name: 'Deduct amount',
        icon: <WalletIcon />,
        key: 'deduct'
      },
      {
        name: 'Delete Rider',
        icon: <TrashIcon />,
        key: 'delete',
        className: 'text-[#E60A2B]'
      }
    ];
  };

  const fnMapping: Record<string, () => void> = {
    'view-details': viewDetails,
    'edit-details': editRiders,
    blacklist: blacklistRider,
    delete: deleteRiders,
    deduct: deductAmount
  };

  const getCheckedStatus = (id: string) => {
    return !!bulkRiders.find((r) => r.id === id);
  };

  const toggleRiderBulkAction = (rider: RiderType) => {
    let newBulkRiders = [...bulkRiders];
    if (getCheckedStatus(rider.id)) newBulkRiders = newBulkRiders.filter((r) => r.id !== rider.id);
    else newBulkRiders.push(rider);
    setBulkRiders(newBulkRiders);
  };

  const handleSelectOption = (option: DropdownListType, rider: RiderType) => {
    if (bulkRiders.length <= 1) setSingleRider(rider);
    const key = option.key ?? 'view-details';
    fnMapping[key]();
  };

  useEffect(() => {
    if (bulkRiders.length > 1) setSingleRider(null);
  }, [bulkRiders]);

  useEffect(() => {
    const selectedOnes = !singleRider ? bulkRiders : [singleRider];
    setSelectedRiders(selectedOnes);
  }, [bulkRiders, singleRider]);

  return (
    <div className='relative mt-1'>
      {riders?.map((rider, index) => (
        <div
          key={index}
          className='flex flex-col md:flex-row gap-2 justify-between py-3 md:items-center'>
          <div className='flex gap-3 shrink-0 grow md:max-w-[150px] justify-between items-center'>
            <Input
              type='checkbox'
              checked={getCheckedStatus(rider.id)}
              onChange={() => toggleRiderBulkAction(rider)}
              name='surge-list'
              className='text-xs'
              label={getUserName(rider)}
            />
            <span className='md:hidden'>
              <Dropdown
                content={
                  <span className='px-[6px] rounded-full py-[10px] inline-flex hover:bg-[#FEF0F2] cursor-pointer'>
                    <MenuIcon className='rotate-90' />
                  </span>
                }>
                <DropdownList
                  list={getListOptions(rider)}
                  onSelect={(option) => handleSelectOption(option, rider)}
                  className='min-w-[150px]'
                />
              </Dropdown>
            </span>
          </div>
          <div className='flex flex-wrap gap-1 grow items-center'>
            <p className='basis-[150px] text-sm text-[#858585]'>{rider.phone_number}</p>
            <p className='grow text-sm text-[#858585]'>{rider.email}</p>
            <div className='hidden md:block basis-[40px] shrink-0'>
              <Dropdown
                content={
                  <span className='px-[6px] rounded-full py-[10px] inline-block hover:bg-[#FEF0F2] cursor-pointer'>
                    <MenuIcon className='rotate-90' />
                  </span>
                }>
                <DropdownList
                  list={getListOptions(rider)}
                  onSelect={(option) => handleSelectOption(option, rider)}
                  className='min-w-[150px]'
                />
              </Dropdown>
            </div>
          </div>
        </div>
      ))}
      <DeleteRiderModal
        show={showDeleteRiderModal}
        onClose={deleteRiders}
        riders={selectedRiders}
      />
      <BlacklistRiderModal
        show={showBlacklistRiderModal}
        onClose={blacklistRider}
        rider={singleRider ?? ({} as RiderType)}
      />
      <RiderDetailsSideModal
        rider={singleRider ?? ({} as RiderType)}
        show={showRiderDetails}
        onClose={viewDetails}
      />
      <EditRiderDetailsModal
        rider={singleRider ?? ({} as Partial<EditRiderType>)}
        show={showEditRiderDetails}
        onClose={editRiders}
      />
      <DeductAmountModal
        riderId={riders ? riders[0]?.id : ''}
        onClose={deductAmount}
        show={showDeductModal}
      />
    </div>
  );
};

export default RidersInfo;
